import React from "react";
import { Link } from "react-router-dom";
import { Box, Typography, useTheme, Container, IconButton, Tooltip } from "@mui/material";
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import InstagramIcon from '@mui/icons-material/Instagram';

const Footer = () => {
  const theme = useTheme();
  return (
    <Box
      component="footer"
      sx={{
        background: theme.palette.mode === 'dark' 
          ? 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)'
          : 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
        borderTop: `1px solid ${theme.palette.divider}`,
        mt: "auto",
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '2px',
          background: 'linear-gradient(90deg, #1976d2, #42a5f5, #1976d2)',
        }
      }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            py: { xs: 4, sm: 6 },
            display: "flex",
            flexDirection: { xs: "column", lg: "row" },
            gap: { xs: 4, lg: 8 },
            alignItems: { xs: "center", lg: "flex-start" },
            textAlign: { xs: "center", lg: "left" },
          }}
        >
          {/* Company Info */}
          <Box sx={{ flex: 1, minWidth: { xs: '100%', lg: '300px' } }}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 700, 
                mb: 2,
                color: 'primary.main',
                fontSize: { xs: '1.2rem', sm: '1.3rem' }
              }}
            >
              Assetrack
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                color: theme.palette.text.secondary,
                mb: 2,
                lineHeight: 1.6,
                maxWidth: { xs: '100%', lg: '350px' }
              }}
            >
              Professional inventory management solution for modern businesses. 
              Track, manage, and optimize your assets with ease.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: { xs: 'center', lg: 'flex-start' } }}>
              <Tooltip title="Facebook">
                <IconButton 
                  size="small" 
                  sx={{ 
                    color: theme.palette.text.secondary,
                    '&:hover': { 
                      color: '#1877f2',
                      bgcolor: 'rgba(24, 119, 242, 0.08)'
                    } 
                  }}
                >
                  <FacebookIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Twitter">
                <IconButton 
                  size="small" 
                  sx={{ 
                    color: theme.palette.text.secondary,
                    '&:hover': { 
                      color: '#1da1f2',
                      bgcolor: 'rgba(29, 161, 242, 0.08)'
                    } 
                  }}
                >
                  <TwitterIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="LinkedIn">
                <IconButton 
                  size="small" 
                  sx={{ 
                    color: theme.palette.text.secondary,
                    '&:hover': { 
                      color: '#0077b5',
                      bgcolor: 'rgba(0, 119, 181, 0.08)'
                    } 
                  }}
                >
                  <LinkedInIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Instagram">
                <IconButton 
                  size="small" 
                  sx={{ 
                    color: theme.palette.text.secondary,
                    '&:hover': { 
                      color: '#e4405f',
                      bgcolor: 'rgba(228, 64, 95, 0.08)'
                    } 
                  }}
                >
                  <InstagramIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Quick Links */}
          <Box sx={{ minWidth: { xs: '100%', sm: '200px' } }}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 600, 
                mb: 3,
                fontSize: '1.1rem'
              }}
            >
              Quick Links
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Link
                to="/terms"
                style={{
                  textDecoration: "none",
                  color: theme.palette.text.secondary,
                  fontWeight: 500,
                  fontSize: "0.95rem",
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.target.style.color = theme.palette.primary.main;
                  e.target.style.transform = 'translateX(4px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.color = theme.palette.text.secondary;
                  e.target.style.transform = 'translateX(0)';
                }}
              >
                Terms & Conditions
              </Link>
              <Link
                to="/privacy"
                style={{
                  textDecoration: "none",
                  color: theme.palette.text.secondary,
                  fontWeight: 500,
                  fontSize: "0.95rem",
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.target.style.color = theme.palette.primary.main;
                  e.target.style.transform = 'translateX(4px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.color = theme.palette.text.secondary;
                  e.target.style.transform = 'translateX(0)';
                }}
              >
                Privacy Policy
              </Link>
            </Box>
          </Box>

          {/* Contact Info */}
          <Box sx={{ minWidth: { xs: '100%', sm: '250px' } }}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 600, 
                mb: 3,
                fontSize: '1.1rem'
              }}
            >
              Contact Us
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: { xs: 'center', lg: 'flex-start' } }}>
                <EmailIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                  support@assetrack.com
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: { xs: 'center', lg: 'flex-start' } }}>
                <PhoneIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                  +1 (555) 123-4567
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: { xs: 'center', lg: 'flex-start' } }}>
                <LocationOnIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                  San Francisco, CA
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Bottom Bar */}
        <Box
          sx={{
            borderTop: `1px solid ${theme.palette.divider}`,
            py: 3,
            textAlign: "center",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.secondary,
              fontSize: "0.9rem",
            }}
          >
            © {new Date().getFullYear()} Omnevu.com. All rights reserved.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;


